'use client';

import React, { useEffect, useState } from 'react';
import { ArrowDownLeft, ArrowUpRight, RefreshCw, TrendingUp, SlidersHorizontal } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { createClient } from '@/lib/supabase/client';

interface Transaction {
  id: string;
  date: string;
  type: string;
  party: string;
  reference: string;
  amount: number;
  direction: string;
  note: string;
}

const directionIcon = (dir: string, type: string) => {
  if (dir === 'in') return <ArrowDownLeft size={14} className="text-positive" />;
  if (type === 'disbursement') return <ArrowUpRight size={14} className="text-warning" />;
  if (type === 'distribution') return <TrendingUp size={14} className="text-info" />;
  return <RefreshCw size={14} className="text-muted-foreground" />;
};

const formatVT = (amount: number) => `VT ${amount.toLocaleString('en-US')}`;

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function DashboardTransactions() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTransactions = async () => {
      const supabase = createClient();

      // Fetch recent repayments
      const { data: repayments } = await supabase
        .from('repayments')
        .select('id, payment_date, amount_paid, payment_method, loans(loan_number, borrowers(name))')
        .order('payment_date', { ascending: false })
        .limit(8);

      // Fetch recent contributions
      const { data: contributions } = await supabase
        .from('capital_contributions')
        .select('id, contribution_date, amount, capital_contributors(name)')
        .order('contribution_date', { ascending: false })
        .limit(4);

      const mapped: Transaction[] = [];

      (repayments || []).forEach((r: any) => {
        mapped.push({
          id: r.id,
          date: r.payment_date || '',
          type: 'repayment',
          party: r.loans?.borrowers?.name || '—',
          reference: r.loans?.loan_number || '—',
          amount: Number(r.amount_paid) || 0,
          direction: 'in',
          note: r.payment_method || '',
        });
      });

      (contributions || []).forEach((c: any) => {
        mapped.push({
          id: c.id,
          date: c.contribution_date || '',
          type: 'contribution',
          party: c.capital_contributors?.name || '—',
          reference: '—',
          amount: Number(c.amount) || 0,
          direction: 'in',
          note: 'Capital contribution',
        });
      });

      // Sort by date descending
      mapped.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      // Get total count from repayments
      const { count } = await supabase
        .from('repayments')
        .select('id', { count: 'exact', head: true });

      setTransactions(mapped.slice(0, 8));
      setTotal(count || 0);
      setLoading(false);
    };

    fetchTransactions();
  }, []);

  if (loading) {
    return (
      <div className="card-elevated h-full flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading transactions...</p>
      </div>
    );
  }

  return (
    <div className="card-elevated h-full">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <h2 className="text-sm font-semibold text-foreground">Recent Transactions</h2>
        <button className="btn-ghost p-1.5 rounded-lg">
          <SlidersHorizontal size={14} />
        </button>
      </div>

      {transactions.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <p className="text-sm text-muted-foreground">No transactions yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-5 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Date</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Type</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Party</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground hidden md:table-cell">Reference</th>
                <th className="text-right px-5 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {transactions.map((txn) => (
                <tr key={txn.id} className="hover:bg-muted/30 transition-colors duration-100">
                  <td className="px-5 py-3 text-xs text-muted-foreground whitespace-nowrap">{txn.date ? formatDate(txn.date) : '—'}</td>
                  <td className="px-3 py-3">
                    <StatusBadge status={txn.type} />
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5">
                      {directionIcon(txn.direction, txn.type)}
                      <span className="text-xs font-medium text-foreground whitespace-nowrap">{txn.party}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5 pl-5">{txn.note}</p>
                  </td>
                  <td className="px-3 py-3 hidden md:table-cell">
                    <span className="text-[11px] font-mono text-muted-foreground">{txn.reference}</span>
                  </td>
                  <td className={`px-5 py-3 text-right text-xs font-mono font-bold whitespace-nowrap ${txn.direction === 'in' ? 'text-positive' : 'text-foreground'}`}>
                    {txn.direction === 'in' ? '+' : '-'}{formatVT(txn.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="px-5 py-3 border-t border-border flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {transactions.length === 0 ? 'No transactions' : `Showing ${transactions.length} of ${total} transactions`}
        </span>
        <button className="text-xs font-semibold text-primary hover:underline">
          View all transactions →
        </button>
      </div>
    </div>
  );
}